import { useState } from 'react';
import { motion } from 'framer-motion';
import { Search } from 'lucide-react';
import '../styles/design-tokens.css';

interface KIDashboardProps {
  onSearch: (query: string) => void;
  isLoading?: boolean;
}

export function KIDashboard({ onSearch, isLoading = false }: KIDashboardProps) {
  const [input, setInput] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim()) {
      onSearch(input.trim());
    }
  };

  return (
    <motion.div
      className="min-h-screen bg-stone-void relative overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      {/* Frequency Wave Background */}
      <div className="absolute inset-0 freq-wave pointer-events-none" />

      {/* Header */}
      <motion.header
        className="sticky top-0 z-40 glass-panel m-4 glass-bg"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-4xl mx-auto px-6 py-3">
          <h1 className="text-3xl font-bold text-gold text-glow">KI CODE</h1>
          <p className="text-xs text-cyan mt-1">Decode the Linguistic Truth</p>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Input Section */}
        <motion.form
          onSubmit={handleSubmit}
          className="mb-12"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div className="relative">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Enter Word, Name, or Date to Decode..."
              className="w-full px-6 py-4 bg-stone-dark border border-silk-gold border-opacity-30 rounded-xl text-neutral-50 placeholder-stone-light focus:outline-none focus:border-silk-gold focus:border-opacity-100 transition-all"
              disabled={isLoading}
            />
            <motion.button
              type="submit"
              disabled={isLoading}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-silk-gold hover:text-silk-cyan transition-colors disabled:opacity-50"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              {isLoading ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  <Search size={20} />
                </motion.div>
              ) : (
                <Search size={20} />
              )}
            </motion.button>
          </div>
        </motion.form>

        {/* Empty State */}
        {!input && (
          <motion.div
            className="text-center py-20"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <div className="text-stone-light text-sm space-y-2">
              <p>Enter a word to begin decoding...</p>
              <p className="text-xs text-stone-mid">
                Discover the Base 44 frequency, ancient roots, and hidden inversions
              </p>
            </div>
          </motion.div>
        )}
      </main>
    </motion.div>
  );
}
